import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { isConnectionRequestStep } from 'utils/campaign-step.utils';
import { formatDateTimeInTimeZone } from 'utils/date.utils';
import { LinkedinContact } from './campaign-statistics-types';
import { messagesForDay } from './day-messages';

interface Props {
  /** Angeklickter Balken; `null` = geschlossen. */
  bar: { dateKey: string; stepName: string } | null;
  messageHistory: LinkedinContact[];
  timeZone: string | undefined;
  campaignIdToName(id: string): string;
  onClose(): void;
  onOpenContact(contact: LinkedinContact): void;
}

/** Die an einem Tag in einem Schritt versendeten Nachrichten — geöffnet per Klick auf einen Balken. */
const DayMessagesDialog: React.FC<Props> = ({
  bar,
  messageHistory,
  timeZone,
  campaignIdToName,
  onClose,
  onOpenContact,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const zone = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const items = useMemo(
    () => (bar ? messagesForDay(messageHistory, bar.dateKey, bar.stepName) : []),
    [bar, messageHistory]
  );
  const multipleCampaigns = new Set(items.map(i => i.contact.funnelId)).size > 1;

  return (
    <Dialog open={!!bar} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className={classes.title}>
        <span>
          {bar
            ? t('dayMessagesTitle', {
                step: t(bar.stepName),
                date: formatDateTimeInTimeZone(`${bar.dateKey}T12:00:00Z`, 'dd.MM.yyyy', zone),
                count: items.length,
              })
            : ''}
        </span>
        <IconButton aria-label={t('close')} onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {items.length === 0 ? (
          <Typography className={classes.muted}>{t('dayMessagesEmpty')}</Typography>
        ) : (
          <div className={classes.list}>
            {items.map(({ contact, message }, i) => (
              <div key={`${contact.name}-${message.date}-${i}`} className={classes.row}>
                <div className={classes.meta}>
                  <span className={classes.time}>
                    {formatDateTimeInTimeZone(message.date, 'HH:mm', zone)}
                  </span>
                  <button
                    type="button"
                    className={classes.contact}
                    onClick={() => onOpenContact(contact)}
                  >
                    {contact.name}
                  </button>
                  {multipleCampaigns && contact.funnelId ? (
                    <span className={classes.muted}>{campaignIdToName(contact.funnelId)}</span>
                  ) : null}
                </div>
                {message.text?.trim() ? (
                  <div className={classes.text}>{message.text}</div>
                ) : isConnectionRequestStep(message.step?.functionName) ? (
                  <div className={`${classes.text} ${classes.muted}`}>
                    {t('connectionRequestNoNote')}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const useStyles = makeStyles()(theme => ({
  title: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 },
  list: { display: 'flex', flexDirection: 'column' },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    padding: '12px 0',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-of-type': { borderBottom: 'none' },
  },
  meta: { display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' },
  time: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.secondary,
    minWidth: 40,
  },
  contact: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    font: 'inherit',
    fontWeight: 600,
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  text: { fontSize: theme.typography.pxToRem(14), whiteSpace: 'pre-wrap', lineHeight: 1.45 },
  muted: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
}));

export default DayMessagesDialog;
